import React, { useState } from "react";
import { Button } from "@chakra-ui/react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { FaTimes } from "react-icons/fa";
import { User, Metric } from "./UserManagement";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db, auth } from "../../../firebase/firebase";
import { logToSystem } from "../../../utils/systemLogging";
import { IconWrapper } from "../../../components/IconWrapper";
import CustomDropdown from "../../../components/CustomDropdown";

interface UserMetricsFormProps {
  user: User;
  addMetric: (userId: string, newMetric: Omit<Metric, "id">) => void;
  color: string;
  bg: string;
  organizationId: string;
  onClose?: () => void;
  currentUserId: string;
}

const UserMetricsForm: React.FC<UserMetricsFormProps> = ({
  user,
  addMetric,
  color,
  bg,
  organizationId,
  onClose,
  currentUserId,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [metric, setMetric] = useState({
    metric: "",
    description: "",
    target: "",
    actual: "",
    status: "⚠️",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    if (!metric.metric || !metric.target) {
      setError("Metric name and Target are required.");
      return;
    }
    setLoading(true);
    try {
      const userKpiRef = doc(
        db,
        `organization/${organizationId}/Metrics/userKpiMetrics`
      );
      const userKpiSnap = await getDoc(userKpiRef);
      if (!userKpiSnap.exists()) {
        await setDoc(userKpiRef, { users: {} });
      }
      const newMetric = {
        id: Date.now().toString(),
        ...metric,
        actual: metric.actual || "",
        type: "unique",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId,
      };
      await updateDoc(userKpiRef, {
        [`users.${user.id}`]: arrayUnion(newMetric),
      });
      addMetric(user.id, newMetric);

      // Log user metric creation activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "KPI Management",
              details: `Added KPI metric ${metric.metric} for user ${user.firstName} ${user.lastName} with target ${metric.target}`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging user KPI metric creation:", logError);
      }

      setMetric({ metric: "", description: "", target: "", actual: "", status: "⚠️" });
      onClose?.();
    } catch (error) {
      setError("Error adding metric. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full mt-5">
      <div className="mb-6">
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add KPI Metric</h2>
        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Fill in the details to add a new KPI metric</p>
      </div>
      <form
        className="space-y-6"
        autoComplete="off"
        onSubmit={e => { e.preventDefault(); handleSubmit(); }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="metric" className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Metric Name
            </label>
            <input
              id="metric"
              type="text"
              placeholder="Enter metric name"
              value={metric.metric}
              onChange={e => setMetric({ ...metric, metric: e.target.value })}
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className={`w-full px-4 py-2 rounded-lg shadow-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-[#C3C9D3]' : 'bg-white text-gray-800 border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-gray-500'}`}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Description (Optional)
            </label>
            <input
              id="description"
              type="text"
              placeholder="Enter description"
              value={metric.description}
              onChange={e => setMetric({ ...metric, description: e.target.value })}
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className={`w-full px-4 py-2 rounded-lg shadow-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-[#C3C9D3]' : 'bg-white text-gray-800 border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-gray-500'}`}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="target" className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Target
            </label>
            <input
              id="target"
              type="text"
              placeholder="Enter target value"
              value={metric.target}
              onChange={e => setMetric({ ...metric, target: e.target.value })}
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className={`w-full px-4 py-2 rounded-lg shadow-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-[#C3C9D3]' : 'bg-white text-gray-800 border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-gray-500'}`}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="actual" className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Actual
            </label>
            <input
              id="actual"
              type="text"
              placeholder="Enter actual value"
              value={metric.actual}
              onChange={e => setMetric({ ...metric, actual: e.target.value })}
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              className={`w-full px-4 py-2 rounded-lg shadow-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-[#C3C9D3]' : 'bg-white text-gray-800 border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143] placeholder-gray-500'}`}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="status" className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Status
            </label>
            <CustomDropdown
              value={metric.status}
              onChange={(value) => setMetric({ ...metric, status: value })}
              isDark={isDark}
              className="w-full px-4 py-2 rounded-lg shadow-sm"
              options={[
                { value: "✅", label: "✅ Completed" },
                { value: "⚠️", label: "⚠️ In Progress" }
              ]}
            />
          </div>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            borderColor="#B59143"
            color="#B59143"
            _hover={{ bg: '#FFF7E6' }}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isDisabled={loading}
            variant="gold"
            size="sm"
          >
            {loading ? "Adding..." : "Add Metric"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserMetricsForm;
